import { db } from '../firebase';
import {
  doc, collection, getDoc, getDocs, setDoc, updateDoc, deleteDoc,
  addDoc, query, where, serverTimestamp, writeBatch, runTransaction, arrayUnion
} from 'firebase/firestore';

// ============================================
// USER OPERATIONS
// ============================================

export const createUserProfile = async (uid, data) => {
  await setDoc(doc(db, 'users', uid), {
    uid,
    email: data.email,
    firstName: data.firstName || '',
    lastName: data.lastName || '',
    platformRole: data.platformRole || 'user',
    createdAt: serverTimestamp(),
    lastLoginAt: serverTimestamp(),
  });
};

export const getUserProfile = async (uid) => {
  const snap = await getDoc(doc(db, 'users', uid));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const updateUserProfile = async (uid, updates) => {
  await updateDoc(doc(db, 'users', uid), updates);
};

export const updateLastLogin = async (uid) => {
  await updateDoc(doc(db, 'users', uid), { lastLoginAt: serverTimestamp() });
};

// ============================================
// ORGANIZATION OPERATIONS
// ============================================

export const createOrganization = async (ownerUid, name) => {
  const orgRef = await addDoc(collection(db, 'organizations'), {
    name,
    ownerId: ownerUid,
    plan: 'trial',
    planStartDate: serverTimestamp(),
    trialEndDate: null,
    createdAt: serverTimestamp(),
  });
  await updateDoc(doc(db, 'users', ownerUid), {
    orgId: orgRef.id,
    orgIds: [orgRef.id],
    defaultOrgId: orgRef.id,
  });
  return orgRef.id;
};

export const getOrganization = async (orgId) => {
  const snap = await getDoc(doc(db, 'organizations', orgId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const updateOrganization = async (orgId, updates) => {
  await updateDoc(doc(db, 'organizations', orgId), updates);
};

// ============================================
// MEMBER OPERATIONS (under organization)
// ============================================

export const addMember = async (orgId, memberData) => {
  const uid = memberData.uid;
  if (!uid) throw new Error('addMember: memberData must include a uid');
  await setDoc(
    doc(db, 'organizations', orgId, 'members', uid),
    { ...memberData, joinedAt: serverTimestamp() }
  );
  return uid;
};

export const getMembers = async (orgId) => {
  const snap = await getDocs(collection(db, 'organizations', orgId, 'members'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const updateMember = async (orgId, memberId, updates) => {
  await updateDoc(doc(db, 'organizations', orgId, 'members', memberId), updates);
};

export const deleteMember = async (orgId, memberId) => {
  await deleteDoc(doc(db, 'organizations', orgId, 'members', memberId));
};

// ============================================
// PROJECT OPERATIONS (under organization)
// ============================================

export const createProject = async (orgId, projectData) => {
  const projectRef = await addDoc(
    collection(db, 'organizations', orgId, 'projects'),
    { ...projectData, createdAt: serverTimestamp() }
  );
  return projectRef.id;
};

export const getProjects = async (orgId) => {
  const snap = await getDocs(collection(db, 'organizations', orgId, 'projects'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const updateProject = async (orgId, projectId, updates) => {
  await updateDoc(doc(db, 'organizations', orgId, 'projects', projectId), updates);
};

export const deleteProject = async (orgId, projectId) => {
  const tasksSnap = await getDocs(
    collection(db, 'organizations', orgId, 'projects', projectId, 'tasks')
  );
  const chunks = [];
  for (let i = 0; i < tasksSnap.docs.length; i += 499) {
    chunks.push(tasksSnap.docs.slice(i, i + 499));
  }
  for (const chunk of chunks) {
    const batch = writeBatch(db);
    chunk.forEach(taskDoc => batch.delete(taskDoc.ref));
    await batch.commit();
  }
  await deleteDoc(doc(db, 'organizations', orgId, 'projects', projectId));
};

// ============================================
// TASK OPERATIONS (under project)
// ============================================

export const createTasks = async (orgId, projectId, tasks) => {
  const batch = writeBatch(db);
  tasks.forEach((task, index) => {
    const taskRef = doc(collection(db, 'organizations', orgId, 'projects', projectId, 'tasks'));
    batch.set(taskRef, { ...task, order: index });
  });
  await batch.commit();
};

export const getTasks = async (orgId, projectId) => {
  const snap = await getDocs(
    collection(db, 'organizations', orgId, 'projects', projectId, 'tasks')
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const updateTask = async (orgId, projectId, taskId, updates) => {
  await updateDoc(
    doc(db, 'organizations', orgId, 'projects', projectId, 'tasks', taskId),
    updates
  );
};

// ============================================
// DEFAULT ASSIGNMENTS (under organization)
// ============================================

export const getDefaultAssignments = async (orgId) => {
  const snap = await getDoc(doc(db, 'organizations', orgId, 'defaultAssignments', 'config'));
  return snap.exists() ? snap.data().assignments || {} : {};
};

export const saveDefaultAssignments = async (orgId, assignments) => {
  await setDoc(
    doc(db, 'organizations', orgId, 'defaultAssignments', 'config'),
    { assignments, updatedAt: serverTimestamp() }
  );
};

// ============================================
// ONBOARDING OPERATIONS
// ============================================

export const updateOnboardingStep = async (orgId, step) => {
  await updateDoc(doc(db, 'organizations', orgId), { onboardingStep: step });
};

export const completeOnboarding = async (orgId) => {
  await updateDoc(doc(db, 'organizations', orgId), {
    onboardingComplete: true,
    onboardingStep: 5,
  });
};



// ============================================
// PLAN UPGRADE + FOUNDING 100 AUTO-TAGGING
// ============================================

export const upgradeOrgToPaid = async (orgId, seatCount = 1) => {
  const foundingRef = doc(db, 'counters', 'founding');
  return runTransaction(db, async (transaction) => {
    const snap = await transaction.get(foundingRef);
    const count = snap.exists() ? snap.data().count : 0;
    const isFounder = count < 100;
    transaction.update(doc(db, 'organizations', orgId), {
      plan: isFounder ? 'founding' : 'active',
      seatCount,
      planStartDate: serverTimestamp(),
      upgradedAt: serverTimestamp(),
      ...(isFounder && {
        foundingNumber: count + 1,
        isFounding100: true,
        foundingLockedAt: serverTimestamp(),
      }),
    });
    if (isFounder) {
      transaction.set(foundingRef, { count: count + 1 }, { merge: true });
    }
    return { isFounder, foundingNumber: isFounder ? count + 1 : null };
  });
};

// ============================================
// MANUAL FOUNDING 100 TAGGING (SuperAdmin)
// ============================================
//
// Data model for the counter doc /counters/founding:
//   { count: number, taken: number[] }
//
// `taken` is the source of truth — an ascending array of foundingNumbers
// currently held (1..100). `count` is kept in sync as `taken.length` for
// dashboards that read it directly. Bug A (numbers being skipped after a
// revoke) is fixed by ALWAYS picking the lowest unused integer 1..100 from
// `taken`, instead of always assigning `count + 1`. Bug B (revoke flipping
// the plan to "active") is fixed by stashing `previousPlan` on grant and
// restoring it on revoke.
// ============================================

// Migrate /counters/founding to include a `taken` array if it doesn't yet —
// derived from the actual `isFounding100 === true` orgs in Firestore. Runs
// outside the transaction so its read can be a query (transactions can't
// query collections). It's idempotent: a no-op once `taken` exists.
const ensureFoundingTakenArray = async () => {
  const foundingRef = doc(db, 'counters', 'founding');
  const snap = await getDoc(foundingRef);
  const data = snap.exists() ? snap.data() : {};
  if (Array.isArray(data.taken)) return;

  const q = query(collection(db, 'organizations'), where('isFounding100', '==', true));
  const orgsSnap = await getDocs(q);
  const taken = orgsSnap.docs
    .map(d => d.data().foundingNumber)
    .filter(n => Number.isInteger(n) && n >= 1 && n <= 100)
    .sort((a, b) => a - b);
  await setDoc(foundingRef, { count: taken.length, taken }, { merge: true });
};

// Lowest integer in [1, 100] not in `taken`.
const lowestAvailableFoundingNumber = (taken) => {
  const set = new Set(taken);
  for (let n = 1; n <= 100; n++) {
    if (!set.has(n)) return n;
  }
  return null; // all 100 taken
};

export const grantFoundingStatus = async (orgId) => {
  await ensureFoundingTakenArray();
  const foundingRef = doc(db, 'counters', 'founding');
  const orgRef = doc(db, 'organizations', orgId);
  return runTransaction(db, async (transaction) => {
    const counterSnap = await transaction.get(foundingRef);
    const orgSnap = await transaction.get(orgRef);
    if (!orgSnap.exists()) {
      return { success: false, reason: 'Organization not found.' };
    }
    const orgData = orgSnap.data();
    if (orgData.isFounding100 === true) {
      return {
        success: false,
        reason: `Already a Founding Member (#${orgData.foundingNumber}).`,
      };
    }

    const counterData = counterSnap.exists() ? counterSnap.data() : {};
    const taken = Array.isArray(counterData.taken) ? counterData.taken.slice() : [];
    const next = lowestAvailableFoundingNumber(taken);
    if (next === null) {
      return { success: false, reason: 'All 100 founding spots are taken.' };
    }

    // Save the org's plan BEFORE we overwrite it, so revoke can restore it.
    const previousPlan = orgData.plan || 'trial';
    const newTaken = [...taken, next].sort((a, b) => a - b);

    transaction.update(orgRef, {
      plan: 'founding',
      isFounding100: true,
      foundingNumber: next,
      foundingLockedAt: serverTimestamp(),
      previousPlan,
    });
    transaction.set(
      foundingRef,
      { count: newTaken.length, taken: newTaken },
      { merge: true }
    );
    return { success: true, foundingNumber: next };
  });
};

export const revokeFoundingStatus = async (orgId) => {
  await ensureFoundingTakenArray();
  const foundingRef = doc(db, 'counters', 'founding');
  const orgRef = doc(db, 'organizations', orgId);
  return runTransaction(db, async (transaction) => {
    const counterSnap = await transaction.get(foundingRef);
    const orgSnap = await transaction.get(orgRef);
    if (!orgSnap.exists()) {
      return { success: false, reason: 'Organization not found.' };
    }
    const orgData = orgSnap.data();
    const heldNumber = orgData.foundingNumber;

    const counterData = counterSnap.exists() ? counterSnap.data() : {};
    const taken = Array.isArray(counterData.taken) ? counterData.taken.slice() : [];
    const newTaken = Number.isInteger(heldNumber)
      ? taken.filter(n => n !== heldNumber)
      : taken;

    // Restore the plan the org was on before founding was granted. Default
    // to 'trial' if we don't have a record (e.g., founding was granted by
    // an old code path before previousPlan tracking existed).
    const restoredPlan = orgData.previousPlan || 'trial';

    transaction.update(orgRef, {
      plan: restoredPlan,
      isFounding100: false,
      foundingNumber: null,
      foundingLockedAt: null,
      previousPlan: null,
    });
    transaction.set(
      foundingRef,
      { count: newTaken.length, taken: newTaken },
      { merge: true }
    );
    return { success: true, restoredPlan, freedNumber: heldNumber || null };
  });
};

// ============================================
// SUPERADMIN: MANUAL PLAN OVERRIDE
// ============================================
// Direct admin override of an org's `plan` field. Does NOT touch Stripe —
// callers should only use this for non-billing transitions (e.g., comping
// an account or correcting state). For founding 100 grants/revokes, use
// grantFoundingStatus / revokeFoundingStatus so the counter stays in sync.

export const VALID_PLANS = ['trial', 'active', 'founding', 'comped', 'cancelled'];

export const updateOrgPlan = async (orgId, newPlan) => {
  if (!VALID_PLANS.includes(newPlan)) {
    return { success: false, reason: `Invalid plan: ${newPlan}` };
  }
  // Steer founding through the dedicated grant flow so the counter and
  // foundingNumber stay correct.
  if (newPlan === 'founding') {
    return {
      success: false,
      reason: 'Use the "Grant Badge" action to assign Founding 100.',
    };
  }
  await updateDoc(doc(db, 'organizations', orgId), {
    plan: newPlan,
    planChangedAt: serverTimestamp(),
  });
  return { success: true };
};

// ============================================
// UC DATE — AUTO-COMPLETE TASKS + HALT NOTIFICATIONS
// ============================================

export const completeActiveAndMLSTasks = async (orgId, projectId) => {
  const tasksSnap = await getDocs(
    collection(db, 'organizations', orgId, 'projects', projectId, 'tasks')
  );
  const batch = writeBatch(db);
  const autoCompletedIds = [];
  tasksSnap.docs.forEach(taskDoc => {
    const task = taskDoc.data();
    const theme = (task.theme || '').toLowerCase();
    if ((theme === 'active' || theme === 'mls') && task.status !== 'complete') {
      batch.update(taskDoc.ref, { status: 'complete', completedAt: serverTimestamp() });
      autoCompletedIds.push(taskDoc.id);
    }
  });
  const projectRef = doc(db, 'organizations', orgId, 'projects', projectId);
  batch.update(projectRef, { notificationsHalted: true, ucAutoCompletedTaskIds: autoCompletedIds });
  await batch.commit();
};

export const revertActiveAndMLSTasks = async (orgId, projectId) => {
  const projectRef = doc(db, 'organizations', orgId, 'projects', projectId);
  const projectSnap = await getDoc(projectRef);
  if (!projectSnap.exists()) return;
  const autoCompletedIds = projectSnap.data().ucAutoCompletedTaskIds || [];
  if (autoCompletedIds.length === 0) return;
  const batch = writeBatch(db);
  autoCompletedIds.forEach(taskId => {
    const taskRef = doc(db, 'organizations', orgId, 'projects', projectId, 'tasks', taskId);
    batch.update(taskRef, { status: 'not-started', completedAt: null });
  });
  batch.update(projectRef, { notificationsHalted: false, ucAutoCompletedTaskIds: [] });
  await batch.commit();
};

// ============================================
// DUE DATE CALCULATION ENGINE
// ============================================

export const applyDueDatesToTasks = async (orgId, projectId, project, variableDaysMap = {}) => {
  const tasksSnap = await getDocs(
    collection(db, 'organizations', orgId, 'projects', projectId, 'tasks')
  );

  const batch = writeBatch(db);
  let updatesCount = 0;

  tasksSnap.docs.forEach(taskDoc => {
    const task = taskDoc.data();

    if (!task.triggerDate) return;

    const triggerDateValue = project[task.triggerDate];
    if (!triggerDateValue) return;

    let daysOffset;

    if (task.variableDays) {
      daysOffset = variableDaysMap[task.name];
      if (daysOffset === undefined || daysOffset === null) return;
    } else if (task.daysOffset !== undefined) {
      daysOffset = task.daysOffset;
    } else {
      return;
    }

    const triggerDate = new Date(triggerDateValue);
    triggerDate.setDate(triggerDate.getDate() + Number(daysOffset));
    const dueDate = triggerDate.toISOString().split('T')[0];

    batch.update(taskDoc.ref, { dueDate });
    updatesCount++;
  });

  if (updatesCount > 0) {
    await batch.commit();
  }
};

// ============================================
// DEAL CLOSd OPERATIONS
// ============================================

export const closeDeal = async (orgId, projectId) => {
  await updateDoc(doc(db, 'organizations', orgId, 'projects', projectId), {
    status: 'closed',
    closedAt: serverTimestamp(),
  });
};

export const archiveDeal = async (orgId, projectId) => {
  await updateDoc(doc(db, 'organizations', orgId, 'projects', projectId), {
    status: 'archived',
    archived: true,
    archivedAt: serverTimestamp(),
  });
};

// ============================================
// MULTI-ORG OPERATIONS
// ============================================

export const getUserOrgs = async (orgIds) => {
  if (!orgIds || orgIds.length === 0) return [];
  const orgs = await Promise.all(
    orgIds.map(async (id) => {
      const snap = await getDoc(doc(db, 'organizations', id));
      if (!snap.exists()) return null;
      return { id: snap.id, ...snap.data() };
    })
  );
  return orgs.filter(Boolean);
};

export const migrateUserToMultiOrg = async (uid, currentOrgId) => {
  await updateDoc(doc(db, 'users', uid), {
    orgIds: [currentOrgId],
    defaultOrgId: currentOrgId,
  });
};

export const addOrgToUser = async (uid, newOrgId) => {
  await updateDoc(doc(db, 'users', uid), {
    orgIds: arrayUnion(newOrgId),
  });
};

export const setActiveOrg = async (uid, orgId) => {
  await updateDoc(doc(db, 'users', uid), { defaultOrgId: orgId });
};

export const loadOrgData = async (orgId) => {
  try {
    const [members, projectDocs] = await Promise.all([
      getMembers(orgId),
      getProjects(orgId),
    ]);
    const projects = await Promise.all(
      projectDocs.map(async (project) => {
        const tasks = await getTasks(orgId, project.id);
        return { ...project, tasks };
      })
    );
    const defaultAssignments = await getDefaultAssignments(orgId);
    return { members, projects, defaultAssignments };
  } catch (err) {
    console.error('[loadOrgData] Failed to load org data:', err);
    return { members: [], projects: [], defaultAssignments: {} };
  }
};

// ============================================
// COLLABORATION OPERATIONS
// ============================================

export const getUserByEmail = async (email) => {
  const q = query(collection(db, 'users'), where('email', '==', email));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const userDoc = snap.docs[0];
  return { id: userDoc.id, ...userDoc.data() };
};

export const inviteCollaborator = async (hostOrgId, dealId, inviteeEmail) => {
  const [invitee, hostOrg, dealSnap] = await Promise.all([
    getUserByEmail(inviteeEmail),
    getOrganization(hostOrgId),
    getDoc(doc(db, 'organizations', hostOrgId, 'projects', dealId)),
  ]);
  if (!invitee) return { success: false, reason: 'No CLOSd account found for that email.' };
  const hostOrgName = hostOrg?.name || 'Unknown Org';
  if (!dealSnap.exists()) return { success: false, reason: 'Deal not found.' };
  const deal = dealSnap.data();
  const dealTitle = deal.propertyAddress || deal.clientName || 'Untitled Deal';
  const existing = (deal.collaborators || []).find(c => c.userId === invitee.id);
  if (existing) return { success: false, reason: 'This person is already a collaborator on this deal.' };
  await updateDoc(doc(db, 'organizations', hostOrgId, 'projects', dealId), {
    collaborators: [...(deal.collaborators || []), {
      userId: invitee.id,
      email: inviteeEmail,
      orgId: invitee.orgId || '',
    }],
  });
  const currentCollabOrgs = invitee.collaboratingOrgs || [];
  const alreadyLinked = currentCollabOrgs.find(o => o.orgId === hostOrgId && o.dealId === dealId);
  if (!alreadyLinked) {
    await updateDoc(doc(db, 'users', invitee.id), {
      collaboratingOrgs: [...currentCollabOrgs, {
        orgId: hostOrgId,
        orgName: hostOrgName,
        dealId,
        dealTitle,
      }],
    });
  }
  return { success: true, inviteeName: `${invitee.firstName || ''} ${invitee.lastName || ''}`.trim(), inviteeId: invitee.id };
};

export const getCollaboratingDeals = async (hostOrgId, userId) => {
  const projectsSnap = await getDocs(collection(db, 'organizations', hostOrgId, 'projects'));
  const allProjects = projectsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  return allProjects.filter(p =>
    (p.collaborators || []).some(c => c.userId === userId)
  );
};

export const removeCollaborator = async (hostOrgId, dealId, inviteeUserId) => {
  const dealRef = doc(db, 'organizations', hostOrgId, 'projects', dealId);
  const dealSnap = await getDoc(dealRef);
  if (!dealSnap.exists()) return;
  const deal = dealSnap.data();
  await updateDoc(dealRef, {
    collaborators: (deal.collaborators || []).filter(c => c.userId !== inviteeUserId),
  });
  const userSnap = await getDoc(doc(db, 'users', inviteeUserId));
  if (userSnap.exists()) {
    const userData = userSnap.data();
    await updateDoc(doc(db, 'users', inviteeUserId), {
      collaboratingOrgs: (userData.collaboratingOrgs || []).filter(
        o => !(o.orgId === hostOrgId && o.dealId === dealId)
      ),
    });
  }
};
// ============================================
// LINK PENDING MEMBER ON SIGNUP
// ============================================

export const linkPendingMember = async (uid, email) => {
  const pendingId = 'pending_' + email.replace(/[^a-zA-Z0-9]/g, '_');
  const orgsSnap = await getDocs(
    query(collection(db, 'organizations'), where('pendingEmails', 'array-contains', email))
  );
  if (!orgsSnap.empty) {
    const orgId = orgsSnap.docs[0].id;
    const pendingRef = doc(db, 'organizations', orgId, 'members', pendingId);
    const pendingSnap = await getDoc(pendingRef);
    if (pendingSnap.exists()) {
      const data = pendingSnap.data();
      const batch = writeBatch(db);
      batch.set(doc(db, 'organizations', orgId, 'members', uid),
        { ...data, uid, status: 'active', activatedAt: serverTimestamp() }
      );
      batch.delete(pendingRef);
      batch.update(doc(db, 'organizations', orgId), {
        pendingEmails: (orgsSnap.docs[0].data().pendingEmails || []).filter(e => e !== email),
      });
      batch.update(doc(db, 'users', uid), { orgId, orgIds: [orgId], defaultOrgId: orgId });
      await batch.commit();
      return { linked: true, orgId };
    }
  }
  return { linked: false };
};


// ============================================
// TOLLGATE STATE
// ============================================

export const updateTollgateState = async (orgId, projectId, fields) => {
  const updates = {};
  Object.entries(fields).forEach(([key, value]) => {
    updates[`tollgates.${key}`] = value;
  });
  await updateDoc(
    doc(db, 'organizations', orgId, 'projects', projectId),
    updates
  );
};

// ============================================
// INVITE MEMBER BY EMAIL
// ============================================

export const addMemberByEmail = async (orgId, memberData) => {
  const existingUser = await getUserByEmail(memberData.email);

  if (existingUser) {
    await setDoc(
      doc(db, 'organizations', orgId, 'members', existingUser.id),
      {
        ...memberData,
        uid: existingUser.id,
        status: 'active',
        joinedAt: serverTimestamp(),
      }
    );
    return { uid: existingUser.id, status: 'active' };
  } else {
    const pendingId = 'pending_' + memberData.email.replace(/[^a-zA-Z0-9]/g, '_');
    const batch = writeBatch(db);
    batch.set(
      doc(db, 'organizations', orgId, 'members', pendingId),
      {
        ...memberData,
        uid: null,
        status: 'pending',
        pendingEmail: memberData.email,
        invitedAt: serverTimestamp(),
      }
    );
    batch.update(doc(db, 'organizations', orgId), {
      pendingEmails: arrayUnion(memberData.email),
    });
    await batch.commit();
    return { uid: null, status: 'pending', pendingId };
  }
};